import { useCallback, useEffect, useRef, useState } from "react";
import { client } from "../../../api/client";
import { extractApiError } from "../helpers";
import { AdminUser, ListResponse, StatsResponse } from "../types";

interface UseAdminUsersResult {
  users: AdminUser[];
  count: number;
  stats: StatsResponse | null;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  reloadStats: () => Promise<void>;
  patchLocal: (user: AdminUser) => void;
}

/**
 * Encapsulates the GET /auth/admin/users/ + /stats/ data lifecycle.
 * Callers own `search` (to keep it debounceable) and pass it in.
 *
 * Each in-flight request is assigned a monotonic sequence number; only the
 * response whose sequence still matches the latest call is allowed to write
 * state. This avoids the stale-response race where typing "a" → "ad" → "adm"
 * fires overlapping requests and the older ones land last, briefly
 * showing unfiltered or partially-filtered results.
 */
export default function useAdminUsers(search: string): UseAdminUsersResult {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [count, setCount] = useState(0);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reloadSeqRef = useRef(0);
  const statsSeqRef = useRef(0);

  // Reset to page 1 whenever the search term changes.
  useEffect(() => {
    setPage(1);
  }, [search]);

  const reload = useCallback(async () => {
    const mySeq = ++reloadSeqRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page) });
      if (search) params.set("search", search);
      const resp = await client.get<ListResponse<AdminUser>>(
        `/auth/admin/users/?${params.toString()}`
      );
      if (mySeq !== reloadSeqRef.current) return;
      setUsers(resp.data.results);
      setCount(resp.data.count);
    } catch (err) {
      if (mySeq !== reloadSeqRef.current) return;
      setError(extractApiError(err, "Failed to load users."));
    } finally {
      if (mySeq === reloadSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, [page, search]);

  const reloadStats = useCallback(async () => {
    const mySeq = ++statsSeqRef.current;
    try {
      const resp = await client.get<StatsResponse>("/auth/admin/users/stats/");
      if (mySeq !== statsSeqRef.current) return;
      setStats(resp.data);
    } catch {
      // Non-fatal.
    }
  }, []);

  useEffect(() => {
    reload();
    reloadStats();
  }, [reload, reloadStats]);

  const patchLocal = useCallback((user: AdminUser) => {
    setUsers((prev) => prev.map((u) => (u.id === user.id ? user : u)));
  }, []);

  return {
    users,
    count,
    stats,
    page,
    setPage,
    isLoading,
    error,
    reload,
    reloadStats,
    patchLocal,
  };
}
